import { markdown } from "@codemirror/lang-markdown"
import { styled } from "@linaria/react"
import { EditorView, placeholder } from "@uiw/react-codemirror"
import { runInAction } from "mobx"
import { observer } from "mobx-react-lite"

import { Button } from "@/components/Button/Button.tsx"
import { CodeMirror } from "@/components/CodeMirror/CodeMirror.tsx"
import { Spring, Vertical } from "@/components/Flex.ts"
import { Spinner } from "@/components/Spinner/Spinner.tsx"
import { inputBaseStyles, inputFocusStyles } from "@/styles/inputMixins.ts"
import { ellipsize, smallFontSize } from "@/styles/textStyles.ts"

import type { CodingGuidelinesModel } from "@/components/CodingGuidelinesDialog/CodingGuidelinesModel.ts"

export const CodingGuidelinesDialogContent = observer(({ model }: {
  model: CodingGuidelinesModel
}) => {
  const isReadOnly = model.readonly.get()
  const isSaving = model.isSaving.get()

  if (model.isLoading.get()) return <Spinner text={ellipsize("Loading")} />

  return (
    <GuidelinesDialogContainer>
      <SectionDescription>
        Add custom instructions that will guide the agent when implementing any task for this project.
        These guidelines apply to the entire project and will help ensure consistent coding standards across all tasks.
      </SectionDescription>

      {isReadOnly && (
        <ReadOnlyHint>
          These guidelines are read-only. To update them, edit the guidelines in the main branch of the original code
          repository.
        </ReadOnlyHint>
      )}

      <CodeMirrorContainer>
        <CodeMirror
          value={model.guidelines.get()}
          readOnly={isReadOnly}
          autoFocus={!isReadOnly}
          onChange={(value) => runInAction(() => model.guidelines.set(value))}
          extensions={[EditorView.lineWrapping, markdown(), placeholder("Enter your guidelines here. For example:\n\n- Never use inline styles\n- Do not use semicolons at the end of the line\n- Use MobX for state management, not React state")]}
          basicSetup={{
            foldGutter: false,
            lineNumbers: false,
            highlightActiveLine: false,
            highlightActiveLineGutter: false
          }}
        />

        <ButtonContainer visible={model.hasChanges.get() && !isReadOnly}>
          <Spring />
          <Button kind="alternative" onClick={model.handleDiscard} data-testid="project-settings-cancel">Cancel</Button>

          <Button
            kind="primary"
            onClick={model.handleSave}
            disabled={isSaving || isReadOnly}
            data-testid="save-button"
          >
            {isSaving ? ellipsize("Saving") : "Save"}
          </Button>
        </ButtonContainer>
      </CodeMirrorContainer>

      <SectionDescription>
        Markdown is supported.
      </SectionDescription>
    </GuidelinesDialogContainer>
  )
})

const GuidelinesDialogContainer = styled(Vertical)`
  flex: 1;
  gap: 16px;
`

const SectionDescription = styled.p`
  margin: 0;

  color: var(--secondary-text-color);

  ${smallFontSize};
`

const CodeMirrorContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;

  min-height: 20vh;

  padding: 12px 12px 56px;

  ${inputBaseStyles};

  &:has(.cm-editor.cm-focused) {
    ${inputFocusStyles};
  }

  .cm-line {
    padding: 0;
  }
`

const ButtonContainer = styled.div<{ visible: boolean }>`
  position: absolute;
  bottom: 12px;
  right: 12px;

  display: flex;
  gap: 8px;

  opacity: ${props => props.visible ? 1 : 0};
  pointer-events: ${props => props.visible ? "auto" : "none"};
  transition: opacity 0.2s ease-in-out;
`

const ReadOnlyHint = styled(SectionDescription)`
  padding: 8px 10px;

  border: 1px solid var(--separator-color);
  border-radius: var(--border-radius);

  background: var(--background-color);
`